/**
 * A section's images, as image-picker slots.
 *
 * Pure: one HTML string in, an array of slot descriptors out. No store, no
 * bridge, no `document` beyond `DOMParser` — which is why it is its own module
 * rather than three more functions inside `image-editor.js`. That file reaches
 * the store, the bridge and the shell's toast on import; this rule set is the
 * part with edge cases worth driving directly, and it can be.
 *
 * The slot shape is the picker's (`showImagePicker({ slots })`): `id`, `label`,
 * `ratio`, `thumb`, `filled`, and a `data` bag the caller gets handed back with
 * the pick so it knows where to apply it.
 */

import { injectEditableAttrs } from './editable-utils.js';
import { domTargetRef } from '../shared/target-ref.js';

/** Both edges at or below this and the ratio is not worth stating. */
const RATIO_MAX_TERM = 32;

/**
 * @param {string} html — the section's stored HTML
 * @param {{eid?: string}} edit — the target the user clicked, for ordering and
 *   for the per-slot `data.edit` each pick is applied through
 * @param {{isDecorative?: (el: Element) => boolean}} [opts]
 *   `isDecorative` excludes images that are not photo targets — decorative
 *   SVGs are `<img data-pb-editable="image">` but a click on one routes to the
 *   decorative panel, so queueing one offers a photo for a slot that cannot
 *   take a photo. Injected rather than imported to keep this module pure.
 * @returns {Array<object>} slots, or `[]` when there is nothing to queue
 */
export function buildImageSlots(html, edit = {}, opts = {}) {
  if (!html) return [];
  const isDecorative = typeof opts.isDecorative === 'function' ? opts.isDecorative : () => false;

  let root;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    root = doc.body.firstElementChild;
    if (!root) return [];
    injectEditableAttrs(root);
  } catch {
    // A section whose HTML will not parse is not a reason to refuse the whole
    // picker — it is a reason to open it on the one target the caller has.
    return [];
  }

  const slots = [];
  for (const img of root.querySelectorAll('img[data-pb-editable="image"]')) {
    const eid = img.getAttribute('data-pb-eid');
    if (!eid) continue; // unaddressable — a slot no pick could be applied to
    if (isDecorative(img)) continue;
    slots.push({
      id: eid,
      label: slotLabelFor(img, slots.length),
      ratio: slotRatioFor(img),
      thumb: img.getAttribute('src') || img.getAttribute('data-src') || '',
      // Pre-filled slots still start EMPTY. "Filled" means "you filled it in
      // this visit", which is what the queue counts down; treating an image
      // that already exists as done would drain the queue before it started.
      filled: false,
      data: {
        edit: {
          ...edit,
          eid,
          targetRef: domTargetRef(eid),
          expectedTargetFingerprint: undefined,
          editableIndex: undefined
        }
      }
    });
  }

  // A queue of one is a chip that can only be what it already is. Returning []
  // is how this says "there is nothing to queue", and the picker's single-slot
  // rendering is the honest shape for that.
  if (slots.length < 2) return [];

  // Start where the user pointed, by ROTATING rather than sorting: the slots
  // stay in DOM order, which is the order they appear down the page, so the
  // queue reads top-to-bottom from wherever it was entered.
  const at = slots.findIndex((s) => s.id === edit.eid);
  return at > 0 ? slots.slice(at).concat(slots.slice(0, at)) : slots;
}

/** An image's own words first — alt text is what its author called it. */
export function slotLabelFor(img, index) {
  const alt = String(img.getAttribute('alt') || '').trim();
  if (alt) return alt.length > 48 ? `${alt.slice(0, 47)}…` : alt;
  return `Image ${index + 1}`;
}

/**
 * `16:9`, from the AUTHORED width/height attributes only.
 *
 * Never from a rendered box: this runs against a parsed string with no layout.
 * An omitted ratio is better than a guessed one — the slot line is what a user
 * checks before accepting a crop.
 */
export function slotRatioFor(img) {
  const w = Number(img.getAttribute('width'));
  const h = Number(img.getAttribute('height'));
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return '';
  const rw = Math.round(w);
  const rh = Math.round(h);
  const divisor = greatestCommonDivisor(rw, rh);
  const a = rw / divisor;
  const b = rh / divisor;
  // Past this the "ratio" is a pair of large coprime numbers, which tells a
  // reader nothing. One number to two places does.
  if (a > RATIO_MAX_TERM || b > RATIO_MAX_TERM) return `${(w / h).toFixed(2)}:1`;
  return `${a}:${b}`;
}

function greatestCommonDivisor(a, b) {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}
